import type { Survey, SurveyStatus } from '../types/survey';

// Phase 6B F5 — pure filter + sort logic for the surveys list, mirroring
// broadcastListView. Kept out of the (untested) page/table components so the
// null-sentAt edge + header-toggle behaviour are unit-testable.

export type SurveyStatusFilter = SurveyStatus | 'all';

export type SurveySortKey = 'title' | 'brand' | 'status' | 'recipients' | 'responses' | 'sentAt';
export type SortDir = 'asc' | 'desc';
export type SurveySort = { key: SurveySortKey; dir: SortDir };

// Default: most-recently-SENT first. Drafts (null sentAt) cluster at the bottom.
export const DEFAULT_SURVEY_SORT: SurveySort = { key: 'sentAt', dir: 'desc' };

export const SURVEY_STATUS_FILTER_OPTIONS: Array<{ value: SurveyStatusFilter; label: string }> = [
  { value: 'all', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'sending', label: 'Sending' },
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' },
];

// Lifecycle order, so a status sort groups logically rather than alphabetically.
const STATUS_RANK: Record<SurveyStatus, number> = {
  draft: 0,
  sending: 1,
  open: 2,
  closed: 3,
  cancelled: 4,
};

// Columns whose natural first click is descending (most/newest first); everything
// else defaults to ascending (A→Z).
const DESC_FIRST: ReadonlySet<SurveySortKey> = new Set(['sentAt', 'recipients', 'responses']);

export function defaultSortDir(key: SurveySortKey): SortDir {
  return DESC_FIRST.has(key) ? 'desc' : 'asc';
}

// Header-click reducer: clicking the active column flips direction; clicking a
// new column switches to it at its natural default direction.
export function nextSort(current: SurveySort, key: SurveySortKey): SurveySort {
  if (current.key === key) {
    return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  }
  return { key, dir: defaultSortDir(key) };
}

export function filterSurveysByStatus(items: Survey[], status: SurveyStatusFilter): Survey[] {
  if (status === 'all') return items;
  return items.filter((s) => s.status === status);
}

function ms(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
}

// sentAt comparator with NULLS ALWAYS LAST (in both directions), so drafts never
// scatter through the list — they cluster at the bottom. Two drafts tie-break by
// createdAt desc (newest draft first) for a stable, predictable cluster.
function compareSentAt(a: Survey, b: Survey, dir: SortDir): number {
  const am = ms(a.sentAt);
  const bm = ms(b.sentAt);
  if (am === null && bm === null) {
    return (ms(b.createdAt) ?? 0) - (ms(a.createdAt) ?? 0);
  }
  if (am === null) return 1; // a (draft) after b
  if (bm === null) return -1; // b (draft) after a
  return dir === 'asc' ? am - bm : bm - am;
}

// Sort a copy of `items`. `brandLabel` resolves the brand-column display value
// (defaults to '' so the helper is usable without it).
export function sortSurveys(
  items: Survey[],
  sort: SurveySort,
  brandLabel: (s: Survey) => string = () => '',
): Survey[] {
  const dir = sort.dir;
  const oriented = (c: number) => (dir === 'asc' ? c : -c);
  const copy = [...items];
  copy.sort((a, b) => {
    let c = 0;
    switch (sort.key) {
      case 'sentAt':
        c = compareSentAt(a, b, dir);
        break;
      case 'title':
        c = oriented(a.title.localeCompare(b.title));
        break;
      case 'brand':
        c = oriented(brandLabel(a).localeCompare(brandLabel(b)));
        break;
      case 'recipients':
        c = oriented(a.totalRecipients - b.totalRecipients);
        break;
      case 'responses':
        c = oriented(a.submittedCount - b.submittedCount);
        break;
      case 'status':
        c = oriented(STATUS_RANK[a.status] - STATUS_RANK[b.status]);
        break;
    }
    if (c !== 0) return c;
    // Deterministic tie-break: most-recent-sent first, drafts last.
    return compareSentAt(a, b, 'desc');
  });
  return copy;
}
